import axios from 'axios';

/**
 * Clase para gestionar peticiones HTTP y evitar duplicados
 */
class RequestManager {
  constructor() {
    // Caché de peticiones en curso
    this.pendingRequests = new Map();
    // Caché de respuestas recientes
    this.responseCache = new Map();
    // Tiempo de vida del caché (en milisegundos)
    this.cacheTTL = 2000; // 2 segundos
  }

  /**
   * Realiza una petición HTTP evitando duplicados
   * @param {Object} config - Configuración de la petición axios
   * @param {boolean} useCache - Si se debe usar caché para esta petición
   * @returns {Promise} - Promesa con la respuesta
   */
  async request(config, useCache = true) {
    // Crear una clave única para esta petición
    const requestKey = this.getRequestKey(config);
    
    // Si hay una petición pendiente con la misma clave, devolver esa promesa
    if (this.pendingRequests.has(requestKey)) {
      console.log('Petición duplicada detectada, reutilizando promesa existente:', requestKey);
      return this.pendingRequests.get(requestKey);
    }
    
    // Si está habilitado el caché y hay una respuesta en caché válida, devolverla
    if (useCache && this.responseCache.has(requestKey)) {
      const cachedData = this.responseCache.get(requestKey);
      if (Date.now() - cachedData.timestamp < this.cacheTTL) {
        console.log('Usando respuesta en caché para:', requestKey);
        return Promise.resolve(cachedData.response);
      } else {
        // Si el caché expiró, eliminarlo
        this.responseCache.delete(requestKey);
      }
    }
    
    // Crear y almacenar la nueva promesa
    const requestPromise = axios(config)
      .then(response => {
        // Almacenar la respuesta en caché
        if (useCache) {
          this.responseCache.set(requestKey, {
            response,
            timestamp: Date.now()
          });
        }
        return response;
      })
      .finally(() => {
        // Eliminar la petición de las pendientes cuando termine
        this.pendingRequests.delete(requestKey);
      });
    
    // Guardar la promesa en el mapa de peticiones pendientes
    this.pendingRequests.set(requestKey, requestPromise);
    
    return requestPromise;
  }
  
  /**
   * Genera una clave única para una petición
   * @param {Object} config - Configuración de la petición
   * @returns {string} - Clave única
   */
  getRequestKey(config) {
    const { method = 'get', url, params, data } = config;
    return `${method}:${url}:${JSON.stringify(params || {})}:${JSON.stringify(data || {})}`;
  }
  
  /**
   * Limpia el caché de respuestas
   */
  clearCache() {
    this.responseCache.clear();
  }
}

// Exportar una instancia única
export const requestManager = new RequestManager();

// Métodos de conveniencia para los diferentes tipos de peticiones
export const get = (url, params, config = {}, useCache = true) => {
  return requestManager.request({ ...config, method: 'get', url, params }, useCache);
};

export const post = (url, data, config = {}, useCache = false) => {
  return requestManager.request({ ...config, method: 'post', url, data }, useCache);
};

export const put = (url, data, config = {}, useCache = false) => {
  return requestManager.request({ ...config, method: 'put', url, data }, useCache);
};

export const del = (url, config = {}, useCache = false) => {
  return requestManager.request({ ...config, method: 'delete', url }, useCache);
};

export default {
  requestManager,
  get,
  post,
  put,
  delete: del
};
